import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';

interface DisputeRow {
  id: number;
  agreementId: number;
  reason: string;
  status: 'OPEN' | 'RESOLVED' | 'CLOSED';
  resolution: string | null;
  createdAt: string;
  resolvedAt: string | null;
  agreement: { id: number; name: string; userId: number; partnerId: number | null };
}

const STATUS_FILTERS = ['OPEN', 'RESOLVED', 'CLOSED', 'ALL'] as const;

const statusColor: Record<string, string> = {
  OPEN: 'text-gold bg-gold/10',
  RESOLVED: 'text-info bg-info/10',
  CLOSED: 'text-success bg-success/10',
};

export default function AdminMfafDisputes() {
  const [disputes, setDisputes] = useState<DisputeRow[]>([]);
  const [filter, setFilter] = useState<typeof STATUS_FILTERS[number]>('OPEN');
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [resolution, setResolution] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback((status: typeof STATUS_FILTERS[number]) => {
    setLoading(true);
    api
      .get('/mfaf/admin/disputes', { params: status === 'ALL' ? {} : { status } })
      .then((res) => setDisputes(res.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(filter);
  }, [filter, load]);

  const resolve = async (id: number, status: 'RESOLVED' | 'CLOSED') => {
    setSubmitting(true);
    try {
      await api.patch(`/mfaf/disputes/${id}/resolve`, { status, resolution: resolution.trim() || undefined });
      setResolvingId(null);
      setResolution('');
      load(filter);
    } catch (e) {
      alert('Could not resolve dispute');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-textPrimary">MFAF Disputes</h1>
        <div className="flex gap-2">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-sm px-3 py-1.5 rounded-lg border transition ${
                filter === s ? 'bg-gold/10 border-gold/40 text-gold' : 'bg-surface1 border-border text-textSecondary hover:border-gold/30'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-surface1 rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-textTertiary">
            <tr>
              <th className="text-left py-3 px-4">Agreement</th>
              <th className="text-left py-3 px-4">Raised</th>
              <th className="text-left py-3 px-4">Reason</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-left py-3 px-4">Resolution</th>
              <th className="text-left py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-textTertiary">Loading...</td></tr>
            ) : disputes.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-textTertiary">No disputes.</td></tr>
            ) : (
              disputes.map((d) => (
                <React.Fragment key={d.id}>
                  <tr className="border-b border-border/60 hover:bg-surface2/50 transition">
                    <td className="py-3 px-4 text-textPrimary">
                      {d.agreement.name}
                      <div className="text-textTertiary text-xs">User #{d.agreement.userId} ↔ Partner #{d.agreement.partnerId ?? '—'}</div>
                    </td>
                    <td className="py-3 px-4 text-textTertiary">{new Date(d.createdAt).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-textSecondary max-w-xs">{d.reason}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[d.status]}`}>{d.status}</span>
                    </td>
                    <td className="py-3 px-4 text-textTertiary max-w-xs">{d.resolution || '—'}</td>
                    <td className="py-3 px-4">
                      {d.status === 'OPEN' && (
                        <button
                          onClick={() => setResolvingId(resolvingId === d.id ? null : d.id)}
                          className="text-xs px-3 py-1.5 rounded-lg bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 transition"
                        >
                          Resolve
                        </button>
                      )}
                      {d.status === 'RESOLVED' && (
                        <button
                          onClick={() => resolve(d.id, 'CLOSED')}
                          disabled={submitting}
                          className="text-xs px-3 py-1.5 rounded-lg bg-surface2 border border-border text-textSecondary hover:border-gold/30 transition disabled:opacity-40"
                        >
                          Close
                        </button>
                      )}
                    </td>
                  </tr>
                  {resolvingId === d.id && (
                    <tr className="border-b border-border/60 bg-surface2/30">
                      <td colSpan={6} className="py-4 px-4">
                        <div className="flex gap-3 items-start">
                          <textarea
                            value={resolution}
                            onChange={(e) => setResolution(e.target.value)}
                            placeholder="Resolution notes (optional)"
                            rows={2}
                            className="flex-1 px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold resize-none"
                          />
                          <button
                            onClick={() => resolve(d.id, 'RESOLVED')}
                            disabled={submitting}
                            className="text-sm px-4 py-2 rounded-lg bg-gold text-bg font-medium hover:bg-gold/90 transition disabled:opacity-40"
                          >
                            Mark Resolved
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
